import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import {
  loadStripe,
  Stripe,
  StripeElementLocale,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import { CheckoutContext } from "v3/context/checkout";
import { TenantContext } from "v3/context/tenant";
import { LocalizationContext } from "v3/context/localization";
import { PaymentContext } from "v3/context/payment";
import { CheckoutErrorContext } from "v3/context/checkoutError";
import { StripePaymentStep } from "v3/components/checkout/Steps/PaymentStep";
import { StripeReviewStep } from "v3/components/checkout/Steps/ReviewStep";
import { useFinalizePriceMutation } from "v3/services/graphql/generated";
import { CHECKOUT_STEPS } from "v3/types/checkout";
import { StyledContainer, StyledSpinner } from "./index.styled";
import {
  logAddShippingInfo,
  logContinueToPurchaseConfirmation,
  logOrderReview,
} from "v3/services/analytics";
import { useCurrentAppId } from "v3/hooks/useCurrentAppId";

export const StripeSteps: FC = () => {
  const {
    checkoutSession,
    setCheckoutSession,
    setIsLoading,
    step,
    transitionToStep,
  } = useContext(CheckoutContext);
  const { tenant } = useContext(TenantContext);
  const { appId } = useCurrentAppId();
  const { localization } = useContext(LocalizationContext);
  const { generatePaymentIntentSecret, paymentIntentSecret } =
    useContext(PaymentContext);
  const { handleCheckoutErrors, setErrorMsg } =
    useContext(CheckoutErrorContext);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [reviewEventsLogged, setReviewEventsLogged] = useState<boolean>(false);
  const [paymentEventsLogged, setPaymentEventsLogged] =
    useState<boolean>(false);

  const [finalizePrice] = useFinalizePriceMutation();
  const [key, setKey] = useState(0);
  const { id } = checkoutSession || {};

  const {
    paymentFailureErrorText = "There was an error processing your payment. Please try again or choose a new payment method to continue.",
  } = localization?.checkout?.errors || {};

  const remountPayment = async () => setKey((prev) => prev + 1);

  useEffect(() => {
    const logPaymentStepEvents = () => {
      if (!checkoutSession || paymentEventsLogged) return;
      setPaymentEventsLogged(true);
      logAddShippingInfo(
        checkoutSession.cart,
        checkoutSession.shippingMethodConfig?.name.toLowerCase() || null
      );
    };

    const logReviewStepEvents = () => {
      if (!checkoutSession || reviewEventsLogged) return;
      setReviewEventsLogged(true);
      logContinueToPurchaseConfirmation(appId, checkoutSession);
      logOrderReview(checkoutSession.cart);
    };

    if (step === CHECKOUT_STEPS.review) {
      logReviewStepEvents();
    } else if (step === CHECKOUT_STEPS.payment) {
      logPaymentStepEvents();
    }
  }, [appId, checkoutSession, paymentEventsLogged, reviewEventsLogged, step]);

  useEffect(() => {
    if (stripePromise || !tenant?.paymentConfig.publicPaymentToken) return;
    try {
      const loadStripePromise = loadStripe(
        tenant.paymentConfig.publicPaymentToken,
        {
          locale:
            (localization?.currentLocale as StripeElementLocale) ||
            (localization?.defaultLocale as StripeElementLocale) ||
            "en",
        }
      );
      setStripePromise(loadStripePromise);
    } catch (e) {
      setErrorMsg(paymentFailureErrorText);
    }
  }, [
    key,
    paymentFailureErrorText,
    setErrorMsg,
    stripePromise,
    tenant?.paymentConfig.publicPaymentToken,
    localization?.currentLocale,
    localization?.defaultLocale,
  ]);

  useEffect(() => {
    const finalizePriceAndStripe = async () => {
      setIsLoading(true);
      try {
        const { data } = await finalizePrice({
          variables: {
            id,
          },
        });
        if (data?.finalizePrice) {
          setCheckoutSession(data.finalizePrice);
          await generatePaymentIntentSecret(data.finalizePrice.id);
        }
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
      }
    };

    finalizePriceAndStripe();
  }, [
    // re-finalize price and get new payment intent when total changes
    checkoutSession?.price.total,
    finalizePrice,
    generatePaymentIntentSecret,
    handleCheckoutErrors,
    id,
    setCheckoutSession,
    setIsLoading,
  ]);

  useEffect(() => {
    remountPayment();
    setIsLoading(false);
  }, [paymentIntentSecret, setIsLoading]);

  const stripeOptions = useMemo(() => {
    return {
      appearance: { theme: "stripe" },
      clientSecret: paymentIntentSecret,
    } as StripeElementsOptions;
  }, [paymentIntentSecret]);

  const editPayment = (shouldNotClearErrors?: boolean) => {
    remountPayment();
    transitionToStep(CHECKOUT_STEPS.payment, shouldNotClearErrors);
  };

  if (!stripePromise || !paymentIntentSecret)
    return <StyledSpinner dotSize="16px" />;

  return (
    <>
      {!!stripePromise && !!paymentIntentSecret && (
        <div key={key}>
          <Elements stripe={stripePromise} options={stripeOptions}>
            <StyledContainer isDisplayed={step === CHECKOUT_STEPS.payment}>
              <StripePaymentStep
                continueToNextStep={() =>
                  transitionToStep(CHECKOUT_STEPS.review)
                }
              />
            </StyledContainer>
            <StyledContainer
              isDisplayed={step === CHECKOUT_STEPS.review && !!checkoutSession}
            >
              <StripeReviewStep
                editShip={() => transitionToStep(CHECKOUT_STEPS.shipping)}
                editPayment={editPayment}
              />
            </StyledContainer>
          </Elements>
        </div>
      )}
    </>
  );
};
